import { useState, useEffect, useCallback } from 'react';
import api from '../../../utils/api';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { toast } from 'sonner';
import { ArrowClockwise, FileXls, FileCsv } from '@phosphor-icons/react';
import { formatAnyDate } from '../../../utils/dateFormat';

const fmtINR = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const STATUS_BADGE = {
  'Not Allocated': 'bg-orange-100 text-orange-800',
  'Partially Allocated': 'bg-blue-100 text-blue-800',
  'Fully Allocated': 'bg-emerald-100 text-emerald-800',
  'Reversed': 'bg-red-100 text-red-700',
  'Cancelled': 'bg-zinc-200 text-zinc-600',
};

export function ReconciliationTab() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [advBal, setAdvBal] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [filters, setFilters] = useState({ customer: '', receipt_number: '', quotation_no: '', allocation_status: '', date_from: '', date_to: '' });
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);
  const [reverseFor, setReverseFor] = useState(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const [{ data }, { data: ab }, { data: sg }] = await Promise.all([
        api.get(`/api/admin/receipt-allocation/receipts?${params}`),
        api.get('/api/admin/receipt-allocation/customer-advance-balance'),
        api.get('/api/admin/receipt-allocation/pending-suggestions'),
      ]);
      setRows(data.receipts || []);
      setTotals(data.totals);
      setAdvBal(ab);
      setSuggestions(sg.suggestions || []);
      setSelected({});
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load reconciliation'); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const selectedList = Object.keys(selected).filter((k) => selected[k]);

  const bulkAllocate = async () => {
    if (!selectedList.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post('/api/admin/receipt-allocation/bulk-auto-allocate', { receipt_numbers: selectedList });
      const failed = data.results.filter((r) => !r.ok);
      toast.success(`${data.allocated_count} receipt(s) auto-allocated${failed.length ? `, ${failed.length} failed` : ''}`);
      if (failed.length) failed.slice(0, 3).forEach((f) => toast.error(`${f.receipt_number}: ${f.reason}`));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk allocation failed'); }
    setBulkBusy(false);
  };

  const openDrill = async (rn) => {
    try {
      const { data } = await api.get(`/api/admin/receipt-allocation/receipts/${encodeURIComponent(rn)}`);
      setDrill(data);
    } catch (e) { toast.error('Failed to load receipt'); }
  };

  const doReverse = async () => {
    try {
      const { data } = await api.post('/api/admin/receipt-allocation/reverse', {
        adjustment_receipt_number: reverseFor, reason,
      });
      toast.success(data.message);
      setReverseFor(null); setReason(''); setDrill(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reversal failed'); }
  };

  const exportFile = (fmt) => {
    const params = new URLSearchParams({ format: fmt });
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    window.open(`/api/admin/receipt-allocation/receipts?${params}`, '_blank');
  };

  return (
    <div data-testid="reconciliation-tab">
      {/* Auto-matched pending allocations */}
      {suggestions.length > 0 && (
        <Card className="border-blue-300 rounded-sm mb-3 bg-blue-50/40" data-testid="suggestions-panel">
          <CardContent className="p-3">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="text-xs font-bold uppercase tracking-wider text-blue-800">
                Suggested Allocations — {suggestions.length} receipt(s) with matching quotations found
              </div>
              <div className="flex items-center gap-2">
                <button className="text-[10px] text-blue-700 hover:underline"
                  onClick={() => setSelected(Object.fromEntries(suggestions.map((s) => [s.receipt_number, true])))}
                  data-testid="suggestions-select-all">Select All</button>
                <button className="text-[10px] text-zinc-500 hover:underline" onClick={() => setSelected({})}>Clear</button>
                <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={!selectedList.length || bulkBusy} onClick={bulkAllocate} data-testid="bulk-allocate-btn">
                  {bulkBusy ? 'Allocating…' : `Auto-Allocate Selected (${selectedList.length})`}
                </Button>
              </div>
            </div>
            <table className="min-w-full text-xs">
              <thead className="text-zinc-600 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="p-1.5"></th>
                  <th className="p-1.5 text-left">Receipt #</th>
                  <th className="p-1.5 text-left">Customer</th>
                  <th className="p-1.5 text-right">Available</th>
                  <th className="p-1.5 text-left">Matching Quotations (FIFO order)</th>
                  <th className="p-1.5 text-right">Total Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {suggestions.map((s) => (
                  <tr key={s.receipt_number} className="border-t border-blue-100" data-testid={`suggestion-${s.receipt_number}`}>
                    <td className="p-1.5">
                      <input type="checkbox" checked={!!selected[s.receipt_number]}
                        onChange={(e) => setSelected({ ...selected, [s.receipt_number]: e.target.checked })}
                        data-testid={`suggest-check-${s.receipt_number}`} />
                    </td>
                    <td className="p-1.5 font-mono font-bold text-orange-700">{s.receipt_number}</td>
                    <td className="p-1.5">{s.customer_name}<span className="text-[9px] text-zinc-500 font-mono ml-1">{s.customer_mobile}</span></td>
                    <td className="p-1.5 text-right font-bold text-emerald-700">{fmtINR(s.available)}</td>
                    <td className="p-1.5">
                      {s.matches.map((m) => (
                        <span key={m.quotation_number} className="inline-flex items-center gap-1 mr-2">
                          <span className="font-mono text-blue-700">{m.quotation_number}</span>
                          <Badge className={`text-[8px] ${m.is_draft ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                            {m.is_draft ? 'DRAFT' : 'CONVERTED'}
                          </Badge>
                          <span className="text-zinc-500">({fmtINR(m.outstanding_balance)})</span>
                        </span>
                      ))}
                      {s.match_count > s.matches.length && <span className="text-zinc-400">+{s.match_count - s.matches.length} more</span>}
                    </td>
                    <td className="p-1.5 text-right font-bold">{fmtINR(s.total_outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Advance balance summary */}
      {advBal && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[['Total Customer Receipts (Advances)', advBal.totals?.receipts, 'text-zinc-900'],
            ['Total Allocated', advBal.totals?.allocated, 'text-blue-700'],
            ['Unallocated Customer Advance', advBal.totals?.unallocated, 'text-emerald-700']].map(([k, v, c]) => (
            <Card key={k} className="border-zinc-300 rounded-sm"><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
              <div className={`text-xl font-bold ${c}`}>{fmtINR(v)}</div>
            </CardContent></Card>
          ))}
        </div>
      )}
      {advBal && advBal.customers?.some((c) => c.total_unallocated > 0) && (
        <div className="mb-3 p-2 rounded-sm border border-emerald-300 bg-emerald-50 text-xs" data-testid="advance-available-note">
          <b>Customer Advance Available:</b>{' '}
          {advBal.customers.filter((c) => c.total_unallocated > 0).map((c) => (
            <span key={c.customer_mobile} className="mr-3">{c.customer_name || c.customer_mobile}: <b>{fmtINR(c.total_unallocated)}</b> (oldest {formatAnyDate(c.oldest_unallocated_date)}, {c.pending_quotations} pending quotation{c.pending_quotations === 1 ? '' : 's'})
              <button className="ml-1 text-blue-600 hover:underline text-[10px]" data-testid={`ledger-pdf-${c.customer_mobile}`}
                onClick={() => window.open(`/api/admin/customer-ledger/${c.customer_mobile}/pdf`, '_blank')}>Ledger PDF</button>
            </span>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-end mb-3">
        <Input className="h-8 w-40 text-xs" placeholder="Customer / Mobile" value={filters.customer} onChange={(e) => setFilters({ ...filters, customer: e.target.value })} data-testid="recon-filter-customer" />
        <Input className="h-8 w-36 text-xs" placeholder="Receipt #" value={filters.receipt_number} onChange={(e) => setFilters({ ...filters, receipt_number: e.target.value })} data-testid="recon-filter-receipt" />
        <Input className="h-8 w-36 text-xs" placeholder="Quotation #" value={filters.quotation_no} onChange={(e) => setFilters({ ...filters, quotation_no: e.target.value })} />
        <Input className="h-8 w-36 text-xs" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
        <Input className="h-8 w-36 text-xs" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
        <Select value={filters.allocation_status || 'all'} onValueChange={(v) => setFilters({ ...filters, allocation_status: v === 'all' ? '' : v })}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid="recon-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {['all', 'Not Allocated', 'Partially Allocated', 'Fully Allocated', 'Reversed', 'Cancelled'].map((s) => (
              <SelectItem key={s} value={s}>{s === 'all' ? 'All Statuses' : s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="h-8" onClick={load}><ArrowClockwise size={14} /></Button>
        <Button variant="outline" size="sm" className="h-8 border-emerald-400 text-emerald-700" onClick={() => exportFile('xlsx')} data-testid="recon-export-xlsx"><FileXls size={14} className="mr-1" />Excel</Button>
        <Button variant="outline" size="sm" className="h-8" onClick={() => exportFile('csv')}><FileCsv size={14} className="mr-1" />CSV</Button>
      </div>

      {totals && (
        <div className="text-xs text-zinc-600 mb-2">
          {totals.count} receipts · Amount <b>{fmtINR(totals.receipt_amount)}</b> · Allocated <b className="text-blue-700">{fmtINR(totals.allocated)}</b> · Unallocated <b className="text-emerald-700">{fmtINR(totals.unallocated)}</b>
        </div>
      )}

      <Card className="border-zinc-300 rounded-sm"><CardContent className="p-0 overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="p-2 text-left">Receipt #</th>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left">Customer</th>
              <th className="p-2 text-left">Type</th>
              <th className="p-2 text-right">Amount</th>
              <th className="p-2 text-right">Allocated</th>
              <th className="p-2 text-right">Unallocated</th>
              <th className="p-2 text-center">Q's Adjusted</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Mode / Ref</th>
              <th className="p-2 text-left">By</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={11} className="p-6 text-center text-zinc-400">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={11} className="p-6 text-center text-zinc-400">No receipts found.</td></tr>
              : rows.map((r) => (
                <tr key={r._id} className={`border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer ${!r.reconciliation_ok ? 'bg-red-50' : ''}`}
                  onClick={() => openDrill(r.receipt_number)} data-testid={`recon-row-${r.receipt_number}`}>
                  <td className="p-2 font-mono font-bold text-orange-700">{r.receipt_number}</td>
                  <td className="p-2">{formatAnyDate(r.receipt_date)}</td>
                  <td className="p-2">{r.customer_name}<div className="text-[9px] text-zinc-500 font-mono">{r.customer_mobile}</div></td>
                  <td className="p-2 uppercase text-[10px]">{r.receipt_type}</td>
                  <td className="p-2 text-right font-bold">{fmtINR(r.amount)}</td>
                  <td className="p-2 text-right text-blue-700">{fmtINR(r.allocated_amount)}</td>
                  <td className="p-2 text-right text-emerald-700 font-bold">{fmtINR(r.unallocated_amount)}</td>
                  <td className="p-2 text-center">{r.quotations_adjusted}</td>
                  <td className="p-2">
                    <Badge className={`text-[9px] ${STATUS_BADGE[r.allocation_status] || 'bg-zinc-100'}`}>{r.allocation_status}</Badge>
                    {!r.reconciliation_ok && <Badge className="ml-1 bg-red-600 text-white text-[9px]">MISMATCH</Badge>}
                  </td>
                  <td className="p-2 text-[10px]">{r.payment_mode}{r.reference ? ` · ${r.reference}` : ''}</td>
                  <td className="p-2 text-[10px]">{r.created_by}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </CardContent></Card>

      {/* Receipt drill-down */}
      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-3xl bg-white max-h-[80vh] overflow-y-auto" data-testid="receipt-drilldown-dialog">
          <DialogHeader><DialogTitle>Receipt {drill?.receipt?.receipt_number} — Allocation Breakup</DialogTitle></DialogHeader>
          {drill && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="p-2 border border-zinc-200 rounded-sm bg-zinc-50">Total Receipt<div className="text-lg font-bold">{fmtINR(drill.receipt.amount)}</div></div>
                <div className="p-2 border border-zinc-200 rounded-sm bg-zinc-50">Allocated<div className="text-lg font-bold text-blue-700">{fmtINR(drill.receipt.allocated_amount)}</div></div>
                <div className="p-2 border border-zinc-200 rounded-sm bg-zinc-50">Unallocated Advance<div className="text-lg font-bold text-emerald-700">{fmtINR(drill.receipt.unallocated_amount)}</div></div>
              </div>
              {drill.reconciliation_mismatch && (
                <div className="p-2 rounded-sm bg-red-50 border border-red-300 text-red-700 text-xs font-bold" data-testid="recon-mismatch">
                  Reconciliation Difference Detected — expected {fmtINR(drill.reconciliation_mismatch.expected)}, actual {fmtINR(drill.reconciliation_mismatch.actual)}, difference {fmtINR(drill.reconciliation_mismatch.difference)} ({drill.reconciliation_mismatch.reason})
                </div>
              )}
              {drill.breakup.length > 0 ? (
                <table className="min-w-full text-xs border border-zinc-200">
                  <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
                    <tr><th className="p-1.5 text-left">Adj. Receipt</th><th className="p-1.5 text-left">Quotation</th><th className="p-1.5 text-right">Amount</th><th className="p-1.5 text-left">Date</th><th className="p-1.5 text-left">By</th><th className="p-1.5 text-left">Status</th><th className="p-1.5"></th></tr>
                  </thead>
                  <tbody>
                    {drill.breakup.map((b) => (
                      <tr key={b.receipt_number} className={`border-t border-zinc-100 ${b.is_reversed ? 'opacity-60' : ''}`}>
                        <td className="p-1.5 font-mono">{b.receipt_number}</td>
                        <td className="p-1.5 font-mono font-bold text-blue-700">{b.quotation_no}</td>
                        <td className="p-1.5 text-right font-bold">{fmtINR(b.amount)}</td>
                        <td className="p-1.5">{formatAnyDate(b.date)}</td>
                        <td className="p-1.5">{b.allocated_by}</td>
                        <td className="p-1.5">{b.is_reversed ? <Badge className="bg-red-100 text-red-700 text-[9px]">REVERSED: {b.reversal_reason}</Badge> : <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">ACTIVE</Badge>}</td>
                        <td className="p-1.5">
                          {!b.is_reversed && (
                            <Button size="sm" variant="outline" className="h-6 text-[10px] border-red-300 text-red-700"
                              onClick={() => setReverseFor(b.receipt_number)} data-testid={`reverse-${b.receipt_number}`}>Reverse</Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <div className="text-xs text-zinc-400">No allocations yet on this receipt.</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reversal confirm */}
      <Dialog open={!!reverseFor} onOpenChange={(o) => { if (!o) { setReverseFor(null); setReason(''); } }}>
        <DialogContent className="max-w-md bg-white" data-testid="reverse-dialog">
          <DialogHeader><DialogTitle className="text-red-700">Reverse Allocation {reverseFor}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-xs text-zinc-600">The quotation outstanding and the receipt's available balance will be restored. The original entry is kept and marked reversed (audit-logged).</div>
            <Input placeholder="Reason (mandatory)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reverse-reason" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setReverseFor(null); setReason(''); }}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700" disabled={reason.trim().length < 3} onClick={doReverse} data-testid="reverse-confirm-btn">Confirm Reversal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
